import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Wallet, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";
import { invoiceEstimatedINR, isReceivedStatus } from "@/lib/finance";

/**
 * Admin-only strip listing every finance invoice tied to this project.
 *
 * Lives on `<ProjectDetail />` just below the Deliverables block so finance
 * context sits where the project work itself sits (instead of forcing admins
 * to bounce to Studio → Finance and filter by hand).
 *
 * Pulls from the same `/api/finance/invoices` endpoint that powers the Finance
 * hub, then filters in-memory by `project_id` (preferred) or `project_name`.
 */

// Row amounts render in the invoice's own currency ($ for USD, ₹ for INR…);
// the summary tiles convert everything to ₹ via each invoice's FX rate.
const fmtMoney = (n, currency = "INR") => {
  const code = (currency || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(Number(n) || 0);
  } catch {
    return `${code} ${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  }
};

const STATUS_TONE = {
  draft:            "slate",
  pending_approval: "yellow",
  approved:         "blue",
  issued:           "blue",
  sent:             "blue",
  partial:          "yellow",
  received:         "green",
  paid:             "green",
  overdue:          "red",
  cancelled:        "red",
};

export default function ProjectInvoicesSection({ projectId, projectName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/invoices");
      const all = Array.isArray(data) ? data : [];
      const lcName = (projectName || "").trim().toLowerCase();
      const matched = all.filter((r) => {
        if (r.project_id && projectId && r.project_id === projectId) return true;
        if (lcName && (r.project_name || "").trim().toLowerCase() === lcName) return true;
        return false;
      });
      setRows(matched);
      setError(null);
    } catch (e) {
      // Non-admins land here when their feature flag is missing — keep quiet.
      const code = e?.response?.status;
      if (code === 403 || code === 401) setError("hidden");
      else { setError("error"); toast.error(formatApiError(e)); }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId, projectName]);

  const totals = useMemo(() => {
    const t = { count: rows.length, billed: 0, paid: 0, outstanding: 0, mixed: false };
    const currencies = new Set();
    rows.forEach((r) => {
      currencies.add((r.currency || "INR").toUpperCase());
      const amtINR = invoiceEstimatedINR(r);
      const gross = amtINR + amtINR * (Number(r.gst_percent) || 0) / 100;
      t.billed += gross;
      if (isReceivedStatus(r.status)) t.paid += gross;
      else if (r.status !== "cancelled" && r.status !== "draft") t.outstanding += gross;
    });
    t.mixed = currencies.size > 1 || (currencies.size === 1 && !currencies.has("INR"));
    return t;
  }, [rows]);

  if (error === "hidden") return null; // role doesn't have finance — render nothing
  if (loading && rows.length === 0) {
    return (
      <div className="card-flat p-4 mt-6" data-testid="project-invoices-loading">
        <div className="label-overline flex items-center gap-2"><Wallet size={12} /> Project invoices</div>
        <div className="text-xs text-slate-400 mt-2">Loading…</div>
      </div>
    );
  }

  return (
    <div className="card-flat p-4 mt-6" data-testid="project-invoices-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-sm bg-[#F54800]/10 text-[#F54800] flex items-center justify-center">
            <Wallet size={13} />
          </span>
          <div>
            <div className="label-overline">Finance · admin only</div>
            <h3 className="font-display text-lg font-bold text-slate-900 tracking-tight">Project invoices</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="project-invoices-refresh">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <Link to="/studio?tab=finance" className="text-[11px] text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid="project-invoices-open">
            Open in Finance <ExternalLink size={10} />
          </Link>
        </div>
      </div>

      {/* Mini stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Stat label="Invoices" value={totals.count} />
        <Stat label={totals.mixed ? "Billed (incl GST, ₹ equiv.)" : "Billed (incl GST)"} value={fmtMoney(totals.billed)} testId="project-invoices-billed" />
        <Stat label={totals.mixed ? "Paid (₹ equiv.)" : "Paid"} value={fmtMoney(totals.paid)} accent="text-green-700" testId="project-invoices-paid" />
        <Stat label={totals.mixed ? "Outstanding (₹ equiv.)" : "Outstanding"} value={fmtMoney(totals.outstanding)} accent="text-orange-700" testId="project-invoices-outstanding" />
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">
          No invoices linked to this project yet. Add one in <Link to="/studio?tab=finance" className="text-[#F54800] hover:underline">Studio → Finance</Link>.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-100 rounded-sm">
          <table className="w-full text-xs" data-testid="project-invoices-table">
            <thead className="bg-slate-50">
              <tr>
                {["Date", "Invoice #", "Client", "Amount", "GST %", "Status", "Due"].map((h) => (
                  <th key={h} className={`label-overline px-3 py-2 whitespace-nowrap ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`project-invoice-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDate(r.date)}</td>
                  <td className="px-3 py-2 font-mono text-slate-900 whitespace-nowrap">{r.invoice_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-700">{r.client_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900 whitespace-nowrap" data-testid={`project-invoice-amount-${r.id}`}>{fmtMoney(r.amount, r.currency)}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.gst_percent != null ? `${r.gst_percent}%` : "—"}</td>
                  <td className="px-3 py-2"><Tag tone={STATUS_TONE[r.status] || "slate"}>{r.status || "—"}</Tag></td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDate(r.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="label-overline">{label}</div>
      <div className={`font-display text-lg font-bold tracking-tight ${accent || "text-slate-900"}`} data-testid={testId}>{value}</div>
    </div>
  );
}
